import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Skeleton } from '@/shared/ui/skeleton'

export const Route = createFileRoute('/dev/tokens')({
  component: TokensPage,
})

/**
 * Página de inspeção do design system. Rota `_dev` — não entra na navegação do
 * produto e sai do bundle quando o app for a produção (code splitting por rota).
 *
 * Serve para ver os tokens aplicados de verdade; os NÚMEROS de contraste que
 * justificam a paleta são verificados em src/styles/contrast.test.ts, não aqui.
 */
function TokensPage() {
  // Namespaces explícitos: o i18n tipado recusa `t('validation:x')` sob o
  // defaultNS 'common' — a chave não existe lá. Erro de compilação, não bug.
  const { t } = useTranslation(['common', 'validation'])

  return (
    <div className="bg-canvas min-h-dvh p-8">
      <div className="mx-auto flex max-w-4xl flex-col gap-6">
        <header className="flex flex-col gap-1">
          <h1 className="text-ink text-2xl font-semibold">Design system — Liberty Finance</h1>
          <p className="text-ink-muted text-sm">
            Tokens OKLCH herdados da marca. Contraste travado em contrast.test.ts.
          </p>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>Botões</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2">
            <Button variant="primary">Agendar</Button>
            <Button variant="secondary">Importar</Button>
            <Button variant="outline">{t('actions.filter')}</Button>
            <Button variant="ghost">{t('actions.cancel')}</Button>
            <Button variant="link">Veja como funciona</Button>
            <Button variant="danger">{t('actions.delete')}</Button>
            <Button variant="primary" disabled>
              Desabilitado
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tamanhos</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2">
            <Button variant="primary" size="xs">
              xs
            </Button>
            <Button variant="primary" size="sm">
              sm
            </Button>
            <Button variant="primary" size="md">
              md
            </Button>
            <Button variant="primary" size="lg">
              lg
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Badges</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2">
            <Badge tone="neutral">Ativo</Badge>
            <Badge tone="info">Novo</Badge>
            <Badge tone="positive">Pago</Badge>
            <Badge tone="danger">Vencido</Badge>
            <Badge tone="caution">Atenção</Badge>
            <Badge tone="soon">{t('comingSoon.badge')}</Badge>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Dinheiro</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-6">
            <span className="text-income tnum text-sm font-medium">+ R$ 3.000,00</span>
            <span className="text-expense tnum text-sm font-medium">− R$ 1.480,00</span>
            <span className="text-ink tnum text-sm font-semibold">R$ 15.180,00</span>
            <span className="text-ink-muted tnum text-sm">R$ 0,00</span>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Formulário</CardTitle>
          </CardHeader>
          <CardContent className="flex max-w-xs flex-col gap-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="desc">Descrição</Label>
              <Input id="desc" placeholder="Ex.: Aluguel de julho" />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="err">Valor</Label>
              <Input id="err" aria-invalid defaultValue="abc" />
              <span className="text-danger text-2xs">{t('validation:invalidMoney')}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Skeleton (grade real de colunas)</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {[0, 1, 2].map((row) => (
              <div key={row} className="flex items-center gap-3">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-3 w-40" />
                <Skeleton className="h-3 w-24" />
                <Skeleton className="ml-auto h-3 w-16" />
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Superfícies</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {(
              [
                ['canvas', 'bg-canvas'],
                ['surface', 'bg-surface'],
                ['surface-2', 'bg-surface-2'],
                ['surface-3', 'bg-surface-3'],
                ['rail', 'bg-rail'],
              ] as const
            ).map(([name, cls]) => (
              <div key={name} className="flex flex-col items-center gap-1">
                <div className={`border-line size-16 rounded-md border ${cls}`} />
                <span className="text-ink-muted text-2xs">{name}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
